import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper
} from '@mui/material';
import {
  School,
  CheckCircle,
  Event,
  AttachMoney,
  HelpOutline
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

const Admissions = () => {
  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
          color: 'white',
          py: { xs: 6, md: 10 },
          textAlign: 'center',
          position: 'relative'
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              mb: 2,
              fontSize: { xs: '2.5rem', md: '3.5rem' }
            }}
          >
            Admissions
          </Typography>
          <Typography
            variant="h5"
            sx={{
              opacity: 0.9,
              fontWeight: 300,
              maxWidth: 700,
              mx: 'auto',
              mb: 4
            }}
          >
            Join our community of learners and begin your educational journey with Eagles Refuge Academy
          </Typography>
          <Button
            component={Link}
            to="/apply"
            variant="contained"
            color="secondary"
            size="large"
            sx={{
              py: 1.5,
              px: 4,
              fontSize: '1.1rem',
              fontWeight: 600,
              borderRadius: 2
            }}
          >
            Apply Now
          </Button>
        </Container>
      </Box>

      {/* Admissions Process Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 2 }}>
            Our Admissions Process
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
            We've designed a simple and straightforward process to help your family join our school community
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {[
            {
              title: 'Submit Application',
              description: 'Complete our online application form with your child\'s information and academic history.',
              icon: <School fontSize="large" color="primary" />
            },
            {
              title: 'Document Review',
              description: 'Our admissions team will review your application and supporting documents.',
              icon: <CheckCircle fontSize="large" color="primary" />
            },
            {
              title: 'Interview & Assessment',
              description: 'Schedule an interview and academic assessment for your child.',
              icon: <Event fontSize="large" color="primary" />
            },
            {
              title: 'Enrollment & Tuition',
              description: 'Upon acceptance, complete enrollment forms and arrange tuition payment.',
              icon: <AttachMoney fontSize="large" color="primary" />
            }
          ].map((step, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card elevation={3} sx={{ height: '100%', borderRadius: 3, transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <Box sx={{ mb: 2 }}>
                    {step.icon}
                  </Box>
                  <Typography variant="h5" component="h3" sx={{ fontWeight: 600, mb: 1 }}>
                    {step.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {step.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Requirements Section */}
      <Box sx={{ bgcolor: 'grey.50', py: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h3" sx={{ fontWeight: 700, mb: 3 }}>
                Admission Requirements
              </Typography>
              <Typography variant="body1" paragraph>
                At Eagles Refuge Academy, we seek students who demonstrate academic potential, good character, and a desire to learn. Our admissions process is designed to identify students who will thrive in our challenging and supportive environment.
              </Typography>
              <List>
                {[
                  'Completed application form',
                  'Previous academic records (report cards, transcripts)',
                  'Teacher recommendations',
                  'Student interview',
                  'Entrance assessment results',
                  'Birth certificate and immunization records'
                ].map((item, index) => (
                  <ListItem key={index} sx={{ py: 1 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card elevation={4} sx={{ borderRadius: 4, overflow: 'hidden' }}>
                <CardMedia
                  component="img"
                  height="400"
                  image="https://source.unsplash.com/random/600x400/?school,students"
                  alt="Students in classroom"
                />
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Tuition & Fees Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" sx={{ fontWeight: 700, mb: 4, textAlign: 'center' }}>
          Tuition & Fees
        </Typography>
        
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'primary.main' }}>
                Annual Tuition
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={2}>
                {[
                  { grade: 'Elementary School (K-5)', amount: '$8,500' },
                  { grade: 'Middle School (6-8)', amount: '$9,500' },
                  { grade: 'High School (9-12)', amount: '$10,500' }
                ].map((item, index) => (
                  <React.Fragment key={index}>
                    <Grid item xs={8}>
                      <Typography variant="body1">{item.grade}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>{item.amount}</Typography>
                    </Grid>
                    {index < 2 && <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>}
                  </React.Fragment>
                ))}
              </Grid>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'primary.main' }}>
                Additional Fees
              </Typography>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={2}>
                {[
                  { fee: 'Application Fee (non-refundable)', amount: '$100' },
                  { fee: 'Registration Fee (annual)', amount: '$350' },
                  { fee: 'Technology Fee (annual)', amount: '$250' },
                  { fee: 'Activity Fee (annual)', amount: '$200' }
                ].map((item, index) => (
                  <React.Fragment key={index}>
                    <Grid item xs={8}>
                      <Typography variant="body1">{item.fee}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>{item.amount}</Typography>
                    </Grid>
                    {index < 3 && <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>}
                  </React.Fragment>
                ))}
              </Grid>
            </Paper>
          </Grid>
        </Grid>
        
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body1" paragraph>
            Financial aid and scholarships are available for qualifying families. Payment plans can be arranged through our business office.
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            size="large"
            startIcon={<HelpOutline />}
            sx={{ mt: 2 }}
          >
            Financial Aid Information
          </Button>
        </Box>
      </Container>

      {/* CTA Section */}
      <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 6, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>
            Ready to Join Eagles Refuge Academy?
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, fontWeight: 300 }}>
            Start your application today or contact our admissions team for more information
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
            <Button
              component={Link}
              to="/apply"
              variant="contained"
              color="secondary"
              size="large"
              sx={{ px: 4, py: 1.5, fontWeight: 600 }}
            >
              Apply Now
            </Button>
            <Button
              component={Link}
              to="/contact"
              variant="outlined"
              color="inherit"
              size="large"
              sx={{ px: 4, py: 1.5, fontWeight: 600, borderColor: 'white' }}
            >
              Contact Admissions
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Admissions;